<template>
  <component
    :is="element"
    class="product-prices"
    :class="$blockClass(blockClass, 'prices')"
  >
    <span
      v-if="varies"
      class="product-prices__varies"
      :class="$blockClass(blockClass, 'varies')"
      v-text="$string('product.prices.from')"
    />

    <span
      v-if="formattedCompareAtPrice && !priceFirst"
      class="product-prices__compare-at-price"
      :class="$blockClass(blockClass, 'compare-at-price')"
    >
      <span 
        class="sr-only"
        v-text="$string('product.prices.original_price')"
      />
      {{ formattedCompareAtPrice }}
    </span>

    <span
      class="product-prices__price"
      :class="[
        $blockClass(blockClass, 'price'),
        { 'product-prices__price--on-sale': formattedCompareAtPrice },
      ]"
    >
      <span 
        class="sr-only"
        v-text="$string('product.prices.current_price')"
      />
      {{ formattedPrice }}
    </span>

    <span
      v-if="formattedCompareAtPrice && priceFirst"
      class="product-prices__compare-at-price"
      :class="$blockClass(blockClass, 'compare-at-price')"
    >
      <span 
        class="sr-only"
        v-text="$string('product.prices.original_price')"
      />
      {{ formattedCompareAtPrice }}
    </span>

    <span
      v-if="available === false || (!isInitialized && !liquidIsAvailable)"
      class="product-prices__sold-out"
      :class="$blockClass(blockClass, 'sold-out')"
      v-text="$string('product.prices.sold_out')"
    />

    <span
      v-if="message && showMessage"
      class="product-prices__message"
      :class="$blockClass(blockClass, 'message')"
      v-text="message"
    />
  </component>
</template>

<script>

/**
 * Vue: Product prices
 * -----------------------------------------------------------------------------
 * Global product prices template.
 *
 * @param {Boolean} [available] - If the product is available.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number|Boolean|null} compareAtPrice - Compare at price if it exists.
 * @param {String} [element] - Custom element override.
 * @param {Number} price - Product price, in cents format.
 * @param {Boolean} [priceFirst] - Display price before compare at price.
 * @param {Boolean} [varies] - Product variants vary in price.
 * @param {Boolean} [isInitialized] - Determine if GQL data has been loaded.
 * @param {String} [liquidIsAvailable] - Availability from liquid.
 * @param {String} [liquidCompareAt] - Compare at price from liquid.
 */
export default {
  name: 'ProductPrices',

  props: {
    available: {
      type: Boolean,
      default: true,
    },
    blockClass: {
      type: String,
      default: '',
    },
    compareAtPrice: {
      type: [Boolean, Number, null],
      default: false,
      required: true,
    },
    element: {
      type: String,
      default: 'p',
    },
    price: {
      type: [Boolean, Number],
      default: 0,
      required: true,
    },
    priceFirst: {
      type: Boolean,
      default: false,
    },
    varies: {
      type: Boolean,
      default: false,
    },
    isInitialized: {
      type: Boolean,
      default: true,
    },
    liquidIsAvailable: {
      type: Boolean,
      default: true,
    },
    liquidCompareAt: {
      type: [Boolean, Number],
      default: 0,
    },
    message: {
      type: [Boolean, String],
      default: false,
    },
    showMessage: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
      initialized: false,
    }
  },

  computed: {

    /**
     * Compute compare at price template.
     * @returns {String}
     */
    formattedCompareAtPrice() {
      if (!this.isInitialized && this.liquidCompareAt) {
        return this.liquidCompareAt && this.price < this.liquidCompareAt
          ? this.$formatMoney(this.liquidCompareAt)
          : ''
      }

      return this.compareAtPrice && this.price < this.compareAtPrice
        ? this.$formatMoney(this.compareAtPrice)
        : ''
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      if (this.price === false) {
        return this.$string('accessibility.loading')
      }

      if (this.price <= 0) {
        return this.$string('product.prices.free')
      }
      return this.price ? this.$formatMoney(this.price).replace('.00', '') : ''
    },

    /**
     * Compute % savings.
     * @returns {String}
     */
    savings() {
      const percent = ((this.compareAtPrice - this.price) / this.compareAtPrice) * 100

      return this.$string(
        'product.prices.savings',
        { replace: { amount: Math.round(percent) } },
      )
    },
  },

  methods: {

    /**
     * Format whole number price amounts without zeros.
     * @param {String}
     */
    removeZeros(price) {
      if (price.match(/[.]00$/)) {
        const newPrice = price.replace(/[.]00$/, '')
        return newPrice
      }

      return price
    },
  },
}
</script>
